import { useState } from "react";
import { cn } from "../../../lib/utils";
import { SkeletonContentLines } from "../FileRecordCard/FileRecordCardSkeleton";

/**
 * Cover for an HTML card whose live document hasn't painted yet: the
 * server-rendered preview JPEG when one is usable, else the loading
 * skeleton. Shared by the block card and the legacy thumb card so their
 * loading states cannot drift.
 *
 * The no-preview case wears FileRecordCardSkeleton's shapes rather than a
 * file glyph: a node reaches this card straight from that skeleton, so
 * anything else pops a second, unrelated loading state into the ~200ms the
 * document takes to fetch and parse. The shapes only, not the sweep — the
 * share view mounts these by the dozen behind the admission scheduler, and
 * they are up for a fraction of a second each.
 *
 * - `onError` demotes a dead preview URL (expired or pruned object) to the
 *   skeleton instead of leaving an invisible broken `<img>` up for the whole
 *   progressive-mount wait.
 * - `pointer-events-none` throughout: the placeholder overlays the shield and
 *   the Open affordance and must never swallow their clicks.
 * - `showPlate` marks surfaces where an opaque plate is acceptable. Frameless
 *   blocks pass false so an absent/failed preview degrades to nothing — an
 *   opaque box under a chromeless block reads as a bug.
 */
export function HtmlCardPlaceholder({
  previewImageUrl,
  showPlate,
}: {
  previewImageUrl: string | null;
  showPlate: boolean;
}) {
  const [previewFailed, setPreviewFailed] = useState(false);

  if (previewImageUrl && !previewFailed) {
    return (
      <img
        data-html-card-placeholder=""
        src={previewImageUrl}
        alt=""
        aria-hidden
        draggable={false}
        loading="lazy"
        decoding="async"
        onError={() => setPreviewFailed(true)}
        className={cn(
          "pointer-events-none absolute inset-0 z-10 h-full w-full object-cover object-top",
          // Framed cards keep the plate under the image so a lazy-deferred or
          // still-decoding JPEG isn't a transparent hole.
          showPlate && "bg-muted",
        )}
      />
    );
  }
  if (!showPlate) return null;
  return (
    <div
      data-html-card-placeholder=""
      className="pointer-events-none absolute inset-0 z-10 flex flex-col overflow-hidden bg-card opacity-75"
    >
      <SkeletonContentLines />
    </div>
  );
}
